/**
 * Created by olenordviste on 09.08.2016.
 */
import { inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { getUserRole, USER_ROLE } from './shared/helpers/roles';
import { CompanyService } from './core/company/company.service';
import { JobService } from './core/job/job.service';
import { UserService } from './ngrx/user/services/user.service';
import { Job } from './core/job/job.model';
import { CustomDomainService } from './core/company/custom-domain/custom-domain.service';
import { AuthActions } from './auth';
import { User } from './shared/models/user/user.model';
import * as UserActions from './ngrx/user/actions/user.action';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from './auth/services/auth.service';


/**
 * disable applicant routes for company
 * redirect unauthenticated users to login page
 */
export const applicantAuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  // const webHelperService = inject(WebHelperService);
  const userService = inject(UserService);
  const store = inject(Store);
  const platformId = inject(PLATFORM_ID);

  if (isPlatformBrowser(platformId)) {
    return userService.getCurrentUser().pipe(
      take(1),
      map(user => {
        if (!user) {
          router.navigate(['/login']);
          return false;
        }

        const isEmployee = userService.isUserEmployee(user);

        // if applicant
        if (!isEmployee) {
          return true;
        }

        // if employee
        if (isEmployee) {
          router.navigate(['/404']);
          return false;
        }

        return false;
      }),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }
};

/**
 * redirect from old URL job pages to the new ones with company_name in URL
 */
export const jobRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const router = inject(Router);
  const jobService = inject(JobService);

  return jobService.getJob(route.params.jobId).pipe(
    take(1),
    map((job: Job) => {

      if (route.url[0].path === 'stilling') {
        router.navigate([job.company.search_name, 'stilling', route.params.jobId]);
      }

      if (route.url[0].path === 'sok-stilling') {
        router.navigate([job.company.search_name, 'sok-stilling', route.params.jobId]);
      }

      return true;
    }),
    catchError(err => {
      router.navigate(['404']);
      return throwError(err);
    }),
  );
};


export const followerUnsubscribeRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => {
  const router = inject(Router);
  const companyService = inject(CompanyService);

  return companyService.unsubscribeCompanyFollower(route.params.companyId, route.params.email).pipe(
    take(1),
    map(() => {
      return true;
    }),
    catchError(err => {
      router.navigate(['404']);
      return throwError(err);
    }),
  );
};


/**
 * Guard to disable company routes if it's forbidden for a role of the current user.
 * If the user has access and an allowed role, it checks if the user's current company is different from the company in the link.
 * If different, the user's company is updated to the new company.
 * @param {ActivatedRouteSnapshot} route - The current route snapshot.
 * @param {RouterStateSnapshot} state - The current router state snapshot.
 * @returns {Observable<boolean>} - An observable that emits a boolean indicating whether the route can be activated.
 */
export const companyRoleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const router = inject(Router);
  const companyService = inject(CompanyService);
  const userService = inject(UserService);
  const authService = inject(AuthService);
  const store = inject(Store);
  const platformId = inject(PLATFORM_ID);

  const roles = route.data.roles as Array<string>;
  const languages = route.data.disabled_languages as Array<string>;
  const companyNameWithQueries = state.url.split('/').filter(e => !!e)[0];
  const companyName = companyNameWithQueries.split('?')[0];
  const returnUrl = state.url;
  const pageToRedirectIfInvalid = route.data?.pageToRedirectIfInvalid || '/authorize/invalid';

  const navigateTo = (url: string, includeReturnUrl: boolean = false) => {
    if (includeReturnUrl) {
      router.navigate([url], { queryParams: { returnUrl } });
    } else {
      router.navigate([url]);
    }

    return false;
  };

  if (isPlatformBrowser(platformId)) {
    return combineLatest([
      userService.getCurrentUser(),
      companyService.getCompanyByName(companyName)
    ]).pipe(
      take(1),
      switchMap(([user, company]) => {
        if (!user) {
          return of(navigateTo('login', true));
        }

        if (!company) {
          return of(navigateTo('404'));
        }

        if (!company.has_valid_subscription) {
          router.navigate([pageToRedirectIfInvalid]);
          return of(false);
        }

        return companyService.getCompanyEmployee(company.id, user.id).pipe(
          take(1),
          catchError(() => {
            return of(navigateTo('404'));
          }),
          switchMap(employee => {
            const hasAccess = !!employee && userService.isUserEmployee(user);
            const isAllowedRole = roles ? roles.includes(getUserRole(user)) : true;
            const isAllowedLanguage = languages ? !languages.includes(company.default_language) : true;

            if (hasAccess && isAllowedRole && isAllowedLanguage) {
              if (user.user_profile.company?.id !== company.id) {
                return userService.setCurrentCompany(company.id).pipe(
                  switchMap((updatedUser: User) => {
                    store.dispatch(UserActions.changed({ current: updatedUser }));
                    return of(true);
                  })
                );
              } else {
                return of(true);
              }
            } else {
              return of(navigateTo('404'));
            }
          })
        );
      }),
      catchError((err) => {
        const includeReturnUrl = err?.status !== 404;

        store.dispatch(AuthActions.logout());

        return authService.logoutFinishedSubject.asObservable().pipe(
          filter(res => !!res),
          take(1),
          map(() => navigateTo('/login', includeReturnUrl))
        );
      })
    );
  }
};

/**
 * Guard to company-select page
 * If the user is unauthenticated - redirect to login
 * If it is applicant - redirect to 404
 * If it is employee - show the page
 * @param {ActivatedRouteSnapshot} route - The current route snapshot.
 * @param {RouterStateSnapshot} state - The current router state snapshot.
 * @returns {Observable<boolean>} - An observable that emits a boolean indicating whether the route can be activated.
 */
export const noActiveCompanyGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const userService = inject(UserService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  if (isPlatformBrowser(platformId)) {
    return userService.getCurrentUser().pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          router.navigate(['login']);
          return of(false);
        }

        if (!user?.user_profile?.companies?.length) {
          router.navigate(['404']);
          return of(false);
        }

        return of(true);
      }),
      catchError(() => {
        router.navigate(['login']);
        return of(false);
      })
    );
  }
};

/**
 * disables routes if it's spectator
 */
export const spectatorGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const companyService = inject(CompanyService);
  const userService = inject(UserService);
  const platformId = inject(PLATFORM_ID);

  if (isPlatformBrowser(platformId)) {
    const companyNameWithQueries = state.url.split('/').filter(e => !!e)[0];
    const companyName = companyNameWithQueries.split('?')[0];

    return combineLatest([
      userService.getCurrentUser(),
      companyService.getCompanyByName(companyName)
    ]).pipe(
      take(1),
      map(([u, c]) => {
        if (!(!!c && !!u)) {
          router.navigate(['404']);
        }

        if (getUserRole(u) !== USER_ROLE.SPECTATOR) {
          return true;
        } else {
          router.navigate(['404']);
        }
      }),
      catchError(err => {
        router.navigate(['/']);
        return throwError(err);
      }),
    );
  }
};

/**
 * disable switching between old and advanced company template for new companies
 */
export const companyProfileEditGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const companyService = inject(CompanyService);

  const companyNameWithQueries = state.url.split('/').filter(e => !!e)[0];
  const companyName = companyNameWithQueries.split('?')[0];

  return companyService.getCompanyByName(companyName).pipe(
    filter(company => !!company),
    map(company => {
      if (companyService.hasAdvancedViewOnly(company)) {
        router.navigate(['/', company.search_name]);
        return false;
      }

      return true;
    }),
    catchError(err => {
      router.navigate(['404']);
      return throwError(err);
    }),
  );
};


/**
 * disable routes for companies that don't have subscription anymore
 */
export const companyValidSubscriptionGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router: Router = inject(Router);
  const companyService: CompanyService = inject(CompanyService);
  const customDomainService = inject(CustomDomainService);
  const pageToRedirectIfInvalid = route.data.pageToRedirectIfInvalid || '/authorize/invalid';

  if (customDomainService.isCustomDomain()) {
    const domain = customDomainService.getHostname();

    return companyService.getCompanyByCustomDomain(domain).pipe(
      map(company => {
        if (!company) {
          router.navigate(['404']);
          return false;
        }

        if (!company.has_valid_subscription) {
          router.navigate([pageToRedirectIfInvalid]);
          return false;
        }

        return true;
      }),
      catchError(() => {
        router.navigate(['404']);
        return EMPTY;
      }),
    );
  } else {
    const companyNameWithQueries = state.url.split('/').filter(e => !!e)[0];
    const companyName = companyNameWithQueries.split('?')[0];

    return companyService.getCompanyByName(companyName).pipe(
      take(1),
      map(company => {
        if (!company) {
          router.navigate(['404']);
          return false;
        }

        if (!company.has_valid_subscription) {
          router.navigate([pageToRedirectIfInvalid]);
          return false;
        }

        return true;
      }),
      catchError(() => {
        router.navigate(['404']);
        return EMPTY;
      }),
    );
  }
};
